<template>
  <!-- Breadcrumb Navigation -->
  <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
    <router-link :to="{ name: 'pages.admin.dashboard' }"
      class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <icon icon="solar:home-smile-bold-duotone" /> 
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
    </router-link>
    <span class="tw-text-gray-300">/</span>
    <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
      <span class="tw-text-sm">{{ $t("dashboard.sidebar.sponsors") }}</span>
    </button>
  </div>

  <div class="tw-px-2 tw-pb-10 tw-pt-2">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <div class="tw-flex tw-items-center tw-gap-2">
                <FormField 
                    v-model="search" 
                    type="text" 
                    :hide-label="true" 
                    :hide-errors="true" 
                    :placeholder="$t('common.sponsors-search-placeholder')"
                    left-icon="solar:magnifer-linear" 
                    @input="onSearch" 
                    class="tw-bg-white tw-rounded-md tw-min-w-[300px]" 
                />
            </div>

            <div>
                <a-button 
                    @click="showCreateModal = true" 
                    intent="flat" 
                    left-icon="solar:add-circle-bold-duotone"
                    class="tw-min-w-[160px]"
                >
                    {{ $t('common.create-new-sponsor') }}
                </a-button>
            </div>
        </div>
        
        <!-- Table Section -->
        <SponsorTable 
            :loading="loading" 
            :items="sponsors" 
            :options="paginationOptions" 
            @next="onNext"
            @previous="onPrevious" 
            @edit="onEdit" 
            @delete="onDelete" 
        />

        <!-- Create Sponsor Modal -->
        <SponsorCreateModal 
            v-if="showCreateModal" 
            :visible="showCreateModal" 
            @close="showCreateModal = false"
            @created="onSponsorCreated" 
        />

        <!-- Edit Sponsor Modal -->
        <SponsorEditModal 
            v-if="showEditModal" 
            :visible="showEditModal" 
            :sponsor="selectedSponsor"
            @close="showEditModal = false" 
            @updated="onSponsorUpdated" 
        />
    </div>

</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import SponsorTable from '@/components/dashboard/admin/settings/sponsors/table/SponsorTable.vue';
import SponsorCreateModal from '@/components/dashboard/admin/settings/sponsors/modals/SponsorCreateModal.vue';
import SponsorEditModal from '@/components/dashboard/admin/settings/sponsors/modals/SponsorEditModal.vue';
import useAlert from '@/composables/useAlert';
import sponsorsApi from '@/api/sponsors';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const sponsors = ref([]);
const search = ref('');
const showCreateModal = ref(false);
const showEditModal = ref(false);
const selectedSponsor = ref(null);

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Search debounce
let searchTimeout = null;

// Methods
const fetchSponsors = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value
        };

        const response = await sponsorsApi.admin.paginate(params);
        
        if (response?.data?.data) {
            // Extract just the data array for the table
            sponsors.value = response.data.data.data;
            
            // Update pagination options
            const { 
                current_page, 
                per_page, 
                total, 
                last_page,
                next_page_url,
                prev_page_url 
            } = response.data.data;

            paginationOptions.value = {
                current_page,
                per_page,
                total,
                last_page,
                next_page_url,
                prev_page_url
            };
        } else {
            sponsors.value = [];
            paginationOptions.value = {
                current_page: 1,
                per_page: 10,
                total: 0,
                last_page: 1,
                next_page_url: null,
                prev_page_url: null
            };
        }
    } catch (error) {
        console.error('Error fetching sponsors:', error);
        useAlert(t('errors.something-went-wrong'), 'danger');
        sponsors.value = [];
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchSponsors(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchSponsors(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchSponsors(paginationOptions.value.current_page - 1);
    }
};

const onSponsorCreated = () => {
    showCreateModal.value = false;
    fetchSponsors(paginationOptions.value.current_page);
};

const onSponsorUpdated = () => {
    showEditModal.value = false;
    selectedSponsor.value = null;
    useAlert(t('messages.sponsor-updated'), 'success');
    fetchSponsors(paginationOptions.value.current_page);
};

const onEdit = (sponsor) => {
    selectedSponsor.value = sponsor;
    showEditModal.value = true;
};

const onDelete = (sponsor) => {
    sponsors.value = sponsors.value.filter(s => s.id !== sponsor.id);
};

// Initialize
onMounted(() => {
    fetchSponsors();
});

</script>

<style scoped>
</style>